import type { AxiosError } from 'axios';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { authApi } from '../api/auth.api';
import type { LoginFormValues } from '../schemas/login-schema';
import type { ApiErrorResponse } from '../types/auth.type';
import type { AuthenticatedUser } from '../types/authenticated-user.type';
import { mapAdminLoginResponse } from '../utils/map-admin-login-response';
import { getFirstAccessibleRoute } from '@/app/router/utils/get-first-accessible-route';
import { getErrorMessage, getNetworkErrorMessage } from '@/core/api/error-messages';
import { useError } from '@/core/hooks/useError';
import { useAuthStore } from '@/core/stores/authStore';
import { adminUsersApi } from '@/features/admin-users/api/admin-users.api';

export function useLogin() {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const updateUser = useAuthStore((s) => s.updateUser);
  const logout = useAuthStore((s) => s.logout);
  const { showError } = useError();

  const submitLogin = async (values: LoginFormValues) => {
    setIsLoading(true);
    try {
      const response = await authApi.login(values);
      const baseUser = mapAdminLoginResponse(response.data);

      // Set token in store so httpClient can inject it for the next API call
      login(baseUser);

      // Fetch current user detail to get permissions and role
      const detailResponse = await adminUsersApi.getUser(baseUser.userId);
      const detail = detailResponse.data;

      const enrichedUser: AuthenticatedUser = {
        ...baseUser,
        displayName: detail.displayName || baseUser.displayName,
        avatarUrl: detail.avatarUrl ?? undefined,
        permissions: detail.permissions ?? [],
        role: detail.roleId ? { roleId: detail.roleId, name: detail.roleName } : null,
      };

      updateUser(enrichedUser);

      // Navigate to the first route this user has access to
      const target = getFirstAccessibleRoute(useAuthStore.getState().hasAnyPermission);
      navigate(target, { replace: true });
    } catch (err) {
      logout();
      const axiosError = err as AxiosError<ApiErrorResponse>;
      const status = axiosError.response?.status;
      const data = axiosError.response?.data;
      const errorCode = data?.title || data?.type;

      let errorMessage;
      if (!axiosError.response) {
        errorMessage = getNetworkErrorMessage();
      } else if (errorCode === 'AdminUser.AccountLocked') {
        const detail = data?.detail ?? '';
        const minutesMatch = detail.match(/(\d+)\s+minute/);
        const minutes = minutesMatch ? minutesMatch[1] : null;
        errorMessage = {
          title: 'Cuenta bloqueada',
          message: minutes
            ? `Tu cuenta ha sido bloqueada por seguridad. Intenta de nuevo en ${minutes} minuto(s).`
            : 'Tu cuenta ha sido bloqueada por seguridad. Intenta de nuevo más tarde.',
          severity: 'warning' as const,
        };
      } else {
        errorMessage = getErrorMessage(errorCode, status, data?.detail);
      }

      showError({
        ...errorMessage,
        id: `login-error-${Date.now()}`,
        timestamp: new Date(),
        statusCode: status,
        errorCode,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return { submitLogin, isLoading };
}
